import { Config, ProductsConfig, StorageProtocol } from '../../class/config.type';
import { mergeJson } from '../merge-json/merge-json';
import { JSONValue } from '../../class/workflow.type';

// tslint:disable-next-line: completed-docs
export class MergeConfigHelper {
  /**
   * This method will combine config files based on product into one config set. for example the four following config files
   * workflow-fields--consumer
   * workflow-fields--oao
   * workflow-fields--mortgage
   * workflow-fields
   * will be merged into one single config workflow-fields
   */
  public combineProductConfigsFiles(config: Config): void {
    for (const configKey of Object.keys(config)) {
      const [topic, product] = configKey.split('--');
      if (!config[topic]) {
        config[topic] = {};
      }

      mergeJson(config[topic], config[configKey]);
      delete config[`${topic}--${product}`];
    }

    this.combineStorageProtocol(config);
  }

  /**
   * This method separate configs per product, for example the four following config files
   * workflow-fields--consumer
   * workflow-fields--oao
   * workflow-fields--mortgage
   * workflow-fields
   * The result will be a config set for each product
   */
  public buildConfigPerProduct(config: Config): ProductsConfig {
    const productsConfig = {};
    const baseConfigs = this.getBaseConfigs(config);

    // Get configs that has --[product name]
    const productConfigKeys = Object.keys(config).filter((ck) => ck.indexOf('--') > -1);
    for (const productConfigKey of productConfigKeys) {
      const [topic, product] = productConfigKey.split('--');
      if (!productsConfig[product]) {
        productsConfig[product] = {};
      }

      if (!productsConfig[product][topic]) {
        productsConfig[product][topic] = {};
      }

      mergeJson(productsConfig[product][topic], baseConfigs[topic], config[productConfigKey]);
    }

    // Add missing base config for each product
    for (const product of Object.keys(productsConfig)) {
      for (const topic of Object.keys(baseConfigs)) {
        if (!productsConfig[product][topic]) {
          productsConfig[product][topic] = baseConfigs[topic];
        }
      }
      this.combineStorageProtocol(productsConfig[product]);
    }
    return productsConfig;
  }

  public mergeExternalConfig(config: Config, productConfig: ProductsConfig, externalConfig: ProductsConfig): void {

    mergeJson(productConfig as {}, externalConfig as {});

    // Merge with External Config if there is any
    for (const product of Object.keys(externalConfig || {})) {
      const topics = externalConfig[product];
      mergeJson(config as JSONValue, topics as JSONValue);
    }
  }

  /** Get configs files that doesn't have -- as base config. */
  private getBaseConfigs(config: Config): Config {
    const baseConfig = {};
    const baseConfigKeys = Object.keys(config).filter((ck) => ck.indexOf('--') < 0);
    for (const topic of baseConfigKeys) {
      baseConfig[topic] = config[topic];
    }
    return baseConfig;
  }

  /**
   * This function will combine individual storage protocol config into one key value storage object based on the backends in storage-info
   * For example consider storage info that has three backends api, ola and mem with following configs:
   *
   * storage.api.json
   * storage.ola.json
   * storage.mem.json
   *
   * combineStorageProtocol function will combine them as the following
   * storage: {
   *  api: {... content of storage.api.json},
   *  ola: {... content of storage.ola.json},
   *  mem: {... content of storage.mem.json}
   * }
   *
   * then remove storage.* from config
   */
  private combineStorageProtocol(config: Config): void {
    if (this.hasConfigStorage(config)) {
      return;
    }

    config.storage = config.storage || {};
    const backend = (config['storage-info'] && config['storage-info'].backend) || {};
    for (const protocol of Object.keys(backend)) {
      const storageProtocol = config.storage[protocol] || ({} as JSONValue);
      mergeJson(storageProtocol as JSONValue, config['storage.' + protocol] as JSONValue);
      config.storage[protocol] = storageProtocol as StorageProtocol;

      delete config['storage.' + protocol];
    }
  }

  private hasConfigStorage(config: Config): boolean {
    if (!config.storage) {
      return false;
    }

    const backend = (config['storage-info'] && config['storage-info'].backend) || {};
    for (const protocol of Object.keys(backend)) {
      if (!config.storage[protocol]) {
        return false;
      }
    }

    return true;
  }
}
